import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PROJECT_STATUSES } from '@d3-moveis/shared';
import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { PROJECT_STATUS_COLORS } from './constants';
import { formatMetricNumber } from './formatNumber';

export function ProjectsByStatusPanel({ projects, locale }) {
  const { t } = useTranslation();

  const projectStatusRows = useMemo(() => {
    if (!projects?.byStatus) return [];
    return PROJECT_STATUSES.map((status) => ({
      status,
      count: projects.byStatus[status] ?? 0,
    })).filter((row) => row.count > 0 || projects.total > 0);
  }, [projects]);

  const maxProjectStatusCount = useMemo(() => {
    if (!projectStatusRows.length) return 1;
    return Math.max(...projectStatusRows.map((row) => row.count), 1);
  }, [projectStatusRows]);

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TrendingUpIcon color="primary" fontSize="small" />
        <Typography variant="h6">{t('metrics.projectsByStatus')}</Typography>
      </Box>

      {!projects || projects.total === 0 ? (
        <Typography color="text.secondary">{t('metrics.noProjects')}</Typography>
      ) : (
        <Stack spacing={2}>
          {projectStatusRows.map((row) => {
            const percentage = Math.round((row.count / maxProjectStatusCount) * 100);
            return (
              <Box key={row.status}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">{t(`metrics.projectStatus.${row.status}`)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMetricNumber(row.count, locale)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: PROJECT_STATUS_COLORS[row.status],
                    },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
